import React, {useEffect} from "react";
import {useSelector} from "../../shared-services/redux-tiny";
import {createGlobalStyle} from "styled-components";
import currentTheme from "../../react-themes/theme";
import * as globalCss from "./globalStyles/index";

const theme = currentTheme();

const usePrerenderedStyles = () => {
  const {prerenderAgent} = useSelector(state => state.session);

  const fetchRenderedCSS = () => Object
    .values(document
      .querySelector('style[data-styled="active"]')
      .sheet.cssRules)
    .map(r => r.cssText)
    .join(" ");

  useEffect(() => {
    window.prerenderReady = false;
  }, []);

  useEffect(() => {
    const safetyTimeout = 100;
    if (!prerenderAgent) return;

    const styleElement = document.createElement("style");
    styleElement.setAttribute("data-source", "pre-style");
    styleElement.innerText = fetchRenderedCSS();

    setTimeout(() => {
      window.prerenderReady = true;
    }, safetyTimeout);

    document.head.appendChild(styleElement);
  }, []);
};

/**
 * @type {GlobalStyleComponent<{}, DefaultTheme>}
 * All styles are imported from the "globalStyles" folder.
 * Please remember to update the "index.js" file if a new css.js file is created.
 */
const GlobalStyles = createGlobalStyle`
  ${Object.values(globalCss)}
`;

const GlobalStylesWrapper = () => {
  usePrerenderedStyles();

  return <>
    <GlobalStyles/>
  </>;
};

export default GlobalStylesWrapper;

/**
 * Quick Media Queries for styled-components
 * @param size (string) => 'sm', 'md' ... (see theme.breakPoints)
 * @returns {function(*): string}
 */
export const mediaQuery = (size) => {
  const breakPoints = theme.breakPoints;
  if (!breakPoints || !breakPoints[size]) return;

  return (style) => `@media (max-width: ${breakPoints[size]}px) { ${style} }`;
};

/**
 * interpolateRules
 * Recursively creates a set of CSS rules based on the options.
 * @param name => the name of the original CSS rule root (e.g. 'margin').
 * @param options => sets of options for creating the rule set (see above examples).
 * @param unit => 'px', 'em'. Can be empty.
 * @returns a set of CSS rules.
 */
export function interpolateRules(name, options, unit = "") {
  const createRule = (name, rule) => `
        .${name}-${rule} {
          ${name}: ${rule}${unit};
        }
      `;

  return options.map((currentOption, index) => {
    if (index === options.length - 1) {
      return typeof(currentOption) === "object"
        ? currentOption.map(option => createRule(name, option))
        : createRule(name, currentOption);
    } else {
      return typeof(currentOption) === "object"
        ? currentOption.map(option => interpolateRules(
          `${name}-${option}`,
          options.slice(1),
          unit))
        : createRule(name, currentOption);
    }
  });
};
